import { useCallback, useEffect, useRef } from 'react';

interface UseSseOptions<T> {
  /**
   * Returns the URL to connect to, or null/undefined to skip connecting.
   * Called at connection time — can read dynamic values (e.g. from localStorage).
   */
  getUrl: () => string | null | undefined;
  enabled: boolean;
  onEvent: (data: T) => void;
  onError?: (err: Event) => void;
}

interface UseSseResult {
  reconnect: () => void;
}

export function useSse<T>({ getUrl, enabled, onEvent, onError }: UseSseOptions<T>): UseSseResult {
  const eventSourceRef = useRef<EventSource | null>(null);
  const onEventRef = useRef(onEvent);
  const onErrorRef = useRef(onError);
  onEventRef.current = onEvent;
  onErrorRef.current = onError;

  const connect = useCallback(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    const url = getUrl();
    if (!url) {
      return;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // EventSource cannot set an Authorization header: the Clerk session cookie
    // is what authenticates the stream, and it only travels when credentials
    // are enabled (the API base can be a different origin than the app).
    const eventSource = new EventSource(url, { withCredentials: true });

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as T;
        onEventRef.current(data);
      } catch (err) {
        console.error('Failed to parse SSE event:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('SSE connection error:', err);
      onErrorRef.current?.(err);
      // EventSource will automatically reconnect
    };

    eventSourceRef.current = eventSource;
  }, [enabled, getUrl]);

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [connect]);

  return { reconnect: connect };
}
